/**
 * What kind of paper it is, and what the app read from it (docs/19 A6).
 *
 * Every paper is read once, when its text arrives, for its facts, its claims,
 * its dated events and — for his papers — his points, each with its page. This
 * is where an officer sees that reading, beside the paper it came from.
 *
 * Four things are the product rather than the layout:
 *
 * **The guess is offered, never taken.** *Looks like: Pavti — Confirm / Pick
 * another.* The app's guess stays in its own column; the officer's answer goes
 * in theirs (rule 4). Nothing is filed by either.
 *
 * **Nothing is hidden** (rule 3). What the reading threw out, and why; what an
 * officer withdrew; what was set aside behind an officer's entry — each is
 * listed with its reason, folded, never dropped.
 *
 * **The machine's reading stays under the officer's.** A corrected entry shows
 * what the app read, struck and muted, above who corrected it and why — the
 * workbench's stacked authorship.
 *
 * **Every act records its reason.** Confirm, Correct and Withdraw each ask why,
 * on the row, and add an entry; nothing is overwritten. So does *Add a line the
 * app missed* (Phase A, B17 — `AddALine`): the paper's own sentence, checked on
 * its page, as an officer's entry beside the app's.
 *
 * No kind, stage or document title is written here: the kinds, their labels
 * and the places (*Article 2*) come off the API.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type {
  CorrectRecordItemBody,
  DiscardedView,
  RecordPaperKind,
  PaperRecordView,
  RecordEntryView,
  RecordItemView,
  SetAsideView,
  SourceFileView,
} from '../../api/client'
import { AddALine } from './AddALine'
import {
  KEPT_WHOLE,
  heldReason,
  kindWords,
  pageWords,
  readAsWords,
  readingSentence,
  withheldWords,
} from './reading'
import './reading.css'

type OnError = (failure: unknown) => void

function useRefresh(caseId: string) {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: ['papers', caseId] })
    void client.invalidateQueries({ queryKey: ['record', caseId] })
  }
}

/* ------------------------------------------------------------ the kind line */

/**
 * *Looks like: <kind> — Confirm / Pick another*, or the officer's own answer.
 *
 * `said` — the row above already says what the paper is, so this line does not
 * say it a second time. It keeps what only it carries: the app's guess where
 * it differed (rule 4), why a reading is held, and the way to change the
 * answer. What that way is called depends on what the row said:
 *
 * - `kind` — the row shows the kind itself, and this changes it.
 * - `document` — the row shows the register document the paper was filed as,
 *   which this does **not** change (that is removing the paper, or withdrawing
 *   the document). What it changes is the kind the paper is read as, and the
 *   button says so. The kind it was read as is still stated, inside *What the
 *   app read*.
 */
export function KindLine({
  caseId,
  row,
  kinds,
  said,
  onError,
}: {
  caseId: string
  row: SourceFileView
  kinds: RecordPaperKind[]
  said?: 'kind' | 'document'
  onError: OnError
}) {
  const refresh = useRefresh(caseId)
  const [picking, setPicking] = useState(false)
  const [choice, setChoice] = useState('')
  /* The record the papers screen already holds — the same query, so no second
     request — for one fact the kind line needs: a reading held until the
     officer says what the paper is (Lane R), whose reason belongs here. */
  const record = useQuery({
    queryKey: ['record', caseId],
    queryFn: () => api.getCaseRecord(caseId),
  })
  const held = heldReason(
    record.data?.papers.find((paper) => paper.source_file_id === row.id)?.latest,
  )
  const set = useMutation({
    mutationFn: (kind: string) => api.setPaperKind(caseId, row.id, { kind, preflight: false }),
    onSuccess: () => {
      setPicking(false)
      setChoice('')
      refresh()
    },
    onError,
  })

  if (row.removed_at) return null

  const picker = picking ? (
    <span className="kind-pick">
      <select
        aria-label="Kind of paper"
        value={choice}
        onChange={(event) => setChoice(event.target.value)}
      >
        <option value="">Choose what it is…</option>
        {kinds.map((kind) => (
          <option key={kind.key} value={kind.key}>
            {kind.label_en} — {kind.label_hi}
          </option>
        ))}
      </select>
      <button
        className="btn ghost"
        disabled={!choice || set.isPending}
        title={choice ? undefined : 'Choose a kind first.'}
        onClick={() => set.mutate(choice)}
      >
        {set.isPending ? 'Setting…' : 'Set'}
      </button>
      <button className="btn ghost" disabled={set.isPending} onClick={() => setPicking(false)}>
        Cancel
      </button>
    </span>
  ) : null

  if (row.paper_kind) {
    // Rule 4: where the app guessed something else, its guess stays on screen
    // beside the officer's answer — muted, never replaced.
    const guessedOtherwise =
      row.paper_kind_detected && row.paper_kind_detected !== row.paper_kind
        ? row.paper_kind_detected
        : null
    return (
      <p className="small muted kind-line" data-testid={`kind-${row.ordinal}`}>
        {said ? null : (
          <>
            Kind: <span className="kind-name">{kindWords(kinds, row.paper_kind)}</span>
          </>
        )}
        {guessedOtherwise ? (
          <span className="guessed" data-testid={`guessed-${row.ordinal}`}>
            {said ? 'The app guessed ' : ' · the app guessed '}
            {kindWords(kinds, guessedOtherwise)}
          </span>
        ) : null}{' '}
        {picker ?? (
          <button className="btn ghost" onClick={() => setPicking(true)}>
            {said === 'kind'
              ? 'Change what it is'
              : said === 'document'
                ? 'Change how it is read'
                : 'Change'}
          </button>
        )}
        {held ? (
          <span className="held-reason" data-testid={`held-${row.ordinal}`}>
            {' '}
            {held}
          </span>
        ) : null}
      </p>
    )
  }
  if (row.paper_kind_detected) {
    const guessed = row.paper_kind_detected
    return (
      <p className="small kind-line looks-like" data-testid={`looks-like-${row.ordinal}`}>
        <span className="muted">Looks like:</span> <strong>{kindWords(kinds, guessed)}</strong>
        {row.paper_kind_reason ? (
          <span className="muted"> — {row.paper_kind_reason}</span>
        ) : null}{' '}
        {picker ?? (
          <>
            <button
              className="btn ghost"
              disabled={set.isPending}
              onClick={() => set.mutate(guessed)}
            >
              Confirm
            </button>{' '}
            <button className="btn ghost" onClick={() => setPicking(true)}>
              Pick another
            </button>
          </>
        )}
      </p>
    )
  }
  /* **Shown for every paper with no kind**, read or not. From Phase A a paper
     whose kind is not settled is not read until the officer says what it is
     (Lane R, option 1) — so an unread paper with no guess (the guess could not
     be made: the service was down) needs this picker most of all. */
  const hasText =
    row.text_origin === 'DOCX_UPLOAD' ||
    (Boolean(row.ingestion_job_id) && row.read_state === 'REVIEW_READY')
  return (
    <p className="small muted kind-line" data-testid={`kind-unknown-${row.ordinal}`}>
      {row.reading_state
        ? 'Kind not known — read with the general question until you say what it is.'
        : hasText
          ? 'Kind not known — it is read once you say what it is.'
          : 'Kind not known — its text is still being read off the scan.'}{' '}
      {picker ?? (
        <button className="btn ghost" onClick={() => setPicking(true)}>
          Say what it is
        </button>
      )}
    </p>
  )
}

/* --------------------------------------------------------- what it read --- */

export function WhatTheAppRead({
  caseId,
  row,
  record,
  kinds,
  onError,
}: {
  caseId: string
  row: SourceFileView
  record: PaperRecordView | undefined
  kinds: RecordPaperKind[]
  onError: OnError
}) {
  const refresh = useRefresh(caseId)
  const [said, setSaid] = useState<string | null>(null)
  const reread = useMutation({
    mutationFn: () => api.rereadPaper(caseId, row.id),
    onSuccess: (receipt) => {
      setSaid(receipt.message)
      refresh()
    },
    onError,
  })

  if (row.removed_at) return null
  const latest = record?.latest ?? null
  const reading = record?.reading ?? null
  const hasText =
    row.text_origin === 'DOCX_UPLOAD' ||
    (Boolean(row.ingestion_job_id) && row.read_state === 'REVIEW_READY')

  // Offered only where there is text to read: asking again of a file nothing
  // can be taken from would only say so a second time.
  const readButton = (label: string) =>
    hasText ? (
      <button className="btn ghost" disabled={reread.isPending} onClick={() => reread.mutate()}>
        {reread.isPending ? 'Asking…' : label}
      </button>
    ) : null

  if (!latest && !reading) {
    if (!hasText) return null
    return (
      <>
        <p className="small muted" data-testid={`unread-${row.ordinal}`}>
          Not yet read for what it says. {readButton('Read it')}
        </p>
        <AddALine caseId={caseId} row={row} />
      </>
    )
  }

  const sentence = readingSentence(latest)
  const failedOrEmpty = latest?.state === 'FAILED' || latest?.state === 'NOTHING_FOUND'
  /* Kept in its authors' own words only — by its kind, or because it was read
     on a guess and speaks of a finding. The server records which on the
     reading; the screen never works it out for itself. */
  const ownWords = Boolean(reading?.usage?.['protected'])
  const entries = record?.items ?? []
  const of = (shape: string) => entries.filter((entry) => entry.shape === shape)
  const discarded = reading?.discarded ?? []
  const withdrawn = record?.withdrawn ?? []
  const setAside = record?.set_aside ?? []

  return (
    <div className="read-panel" data-testid={`read-panel-${row.ordinal}`}>
      {sentence && latest?.id !== reading?.id ? (
        <p className={`small${latest?.state === 'FAILED' ? '' : ' muted'}`}>
          {sentence} {latest?.state === 'FAILED' ? readButton('Read it again') : null}
        </p>
      ) : null}
      {sentence && latest?.id === reading?.id && failedOrEmpty ? (
        <p className="small muted">{sentence}</p>
      ) : null}
      {said ? <p className="small muted">{said}</p> : null}
      {/* A reading that failed, with none before it: the officer can still put
          what matters on the record by hand while it is read again. */}
      {!reading && latest?.state === 'FAILED' && hasText ? (
        <AddALine caseId={caseId} row={row} />
      ) : null}

      {reading ? (
        <details className="read-fold">
          <summary>
            <span>What the app read</span>
            <span className="small muted">
              {entries.length} item{entries.length === 1 ? '' : 's'}
              {reading.summary ? ` · ${reading.summary}` : ''}
            </span>
          </summary>
          <div className="read-body">
            <p className="small muted">
              {readAsWords(kinds, reading, row.paper_kind)} ·{' '}
              <span className="mono">{reading.question_version}</span>
            </p>
            {ownWords ? (
              <p className="small muted">
                Read for its procedure and its author’s own words only — the app adds no line of
                its own about what it concludes.
              </p>
            ) : null}
            <Group title="Facts" entries={of('fact')} {...{ caseId, row, ownWords, onError }} />
            <Group
              title="Dated events"
              entries={of('event')}
              {...{ caseId, row, ownWords, onError }}
            />
            <Group
              title={ownWords ? 'Its own words' : 'What it says'}
              entries={of('claim')}
              {...{ caseId, row, ownWords, onError }}
            />
            <Group
              title={ownWords ? 'Its own words, article by article' : 'His points'}
              entries={of('point')}
              {...{ caseId, row, ownWords, onError }}
            />
            {entries.length === 0 ? (
              <p className="small muted">Nothing is in the record from this paper.</p>
            ) : null}
            <AddALine caseId={caseId} row={row} />
            {discarded.length > 0 ? (
              <Discarded items={discarded} textOrigin={row.text_origin} />
            ) : null}
            {withdrawn.length + setAside.length > 0 ? (
              <OutOfRecord
                withdrawn={withdrawn}
                setAside={setAside}
                textOrigin={row.text_origin}
              />
            ) : null}
            <p className="small">{readButton('Read it again')}</p>
          </div>
        </details>
      ) : null}
    </div>
  )
}

function Group({
  title,
  entries,
  caseId,
  row,
  ownWords,
  onError,
}: {
  title: string
  entries: RecordEntryView[]
  caseId: string
  row: SourceFileView
  ownWords: boolean
  onError: OnError
}) {
  if (entries.length === 0) return null
  return (
    <div className="read-group">
      <h4>{title}</h4>
      {entries.map((entry) => (
        <Entry
          key={entry.id}
          caseId={caseId}
          entry={entry}
          textOrigin={row.text_origin}
          ownWords={ownWords}
          onError={onError}
        />
      ))}
    </div>
  )
}

/** The words that say what an entry is, before its quote. */
function heading(entry: RecordItemView): string {
  switch (entry.shape) {
    case 'fact':
      return entry.kind_label ?? entry.kind
    case 'event':
      return [entry.value, entry.kind_label, entry.statement].filter(Boolean).join(' · ')
    default:
      return [entry.refers_to_label, entry.statement].filter(Boolean).join('. ')
  }
}

function sameReading(a: RecordItemView, b: RecordItemView): boolean {
  return (
    a.value === b.value &&
    a.quote === b.quote &&
    a.statement === b.statement &&
    a.refers_to === b.refers_to &&
    a.page_first === b.page_first
  )
}

type Act = 'confirm' | 'correct' | 'withdraw'

function Entry({
  caseId,
  entry,
  textOrigin,
  ownWords,
  onError,
}: {
  caseId: string
  entry: RecordEntryView
  textOrigin: string | null | undefined
  ownWords: boolean
  onError: OnError
}) {
  const refresh = useRefresh(caseId)
  const [acting, setActing] = useState<Act | null>(null)
  const [reason, setReason] = useState('')
  const [value, setValue] = useState(entry.value ?? '')
  const [statement, setStatement] = useState(entry.statement ?? '')

  /* The officer's keyboard is English: a value (a date, a number) and the
     one-line statement can be corrected here; the paper's own words cannot be
     retyped, so a wrong quote is withdrawn instead. The quasi-judicial papers
     carry no statement, and none is offered (ADR-021). */
  const canEditValue = entry.shape === 'fact' || entry.shape === 'event'
  const canEditStatement = !ownWords && (entry.shape === 'claim' || entry.shape === 'point')

  const act = useMutation({
    mutationFn: () => {
      const why = reason.trim()
      if (acting === 'confirm') {
        return api.confirmRecordItem(caseId, entry.id, { preflight: false, reason: why })
      }
      if (acting === 'withdraw') {
        return api.withdrawRecordItem(caseId, entry.id, { preflight: false, reason: why })
      }
      const body: CorrectRecordItemBody = { preflight: false, reason: why }
      if (canEditValue && value.trim() !== (entry.value ?? '')) body.value = value.trim()
      if (canEditStatement && statement.trim() !== (entry.statement ?? '')) {
        body.statement = statement.trim()
      }
      return api.correctRecordItem(caseId, entry.id, body)
    },
    onSuccess: () => {
      setActing(null)
      setReason('')
      refresh()
    },
    onError,
  })

  const history = entry.history ?? []
  const previous = history.length ? history[history.length - 1] : null
  // An officer's entry standing over nothing is a line the officer added — the
  // app's reading missed it (Phase A, B17) — not a correction of anything.
  const verb = entry.withdrawn
    ? 'Withdrawn'
    : !previous
      ? 'Added'
      : sameReading(previous, entry)
        ? 'Confirmed'
        : 'Corrected'
  const changed =
    acting !== 'correct' ||
    (canEditValue && value.trim() !== (entry.value ?? '')) ||
    (canEditStatement && statement.trim() !== (entry.statement ?? ''))

  return (
    <div className="entry" data-testid={`entry-${entry.id}`}>
      <div className="entry-line">
        <span>{heading(entry)}</span>
        <span className="mono faint">
          {pageWords(entry.page_first, entry.page_last, textOrigin)}
        </span>
      </div>
      {entry.shape === 'fact' && entry.value ? (
        <div className="entry-value deva">{entry.value}</div>
      ) : null}
      {entry.quote && entry.shape !== 'fact' ? (
        <p className="deva entry-quote">{entry.quote}</p>
      ) : null}
      {entry.detail?.['kept_whole'] === true ? (
        <p className="small muted" data-testid={`kept-whole-${entry.id}`}>
          {KEPT_WHOLE}
        </p>
      ) : null}
      {typeof entry.detail?.['claimed_place_label'] === 'string' ? (
        <p className="small muted" data-testid={`unplaced-${entry.id}`}>
          The app placed this under {entry.detail['claimed_place_label']}; the page does not
          confirm it, so it is kept without a place.
        </p>
      ) : null}
      {entry.author && entry.shape !== 'fact' ? (
        <p className="small muted entry-author">— {entry.author}</p>
      ) : null}
      {withheldWords(entry.detail?.['withheld']) ? (
        <p className="small muted" data-testid={`withheld-${entry.id}`}>
          {withheldWords(entry.detail?.['withheld'])}
        </p>
      ) : null}

      {entry.origin === 'officer' ? (
        <div className="authorship">
          {entry.machine ? (
            <div className="detected">
              <span className="who">Read by the app:</span>{' '}
              <span className="what">
                {entry.machine.value ?? entry.machine.statement ?? entry.machine.quote}
              </span>
            </div>
          ) : null}
          <div className="adjusted">
            <span className="who">
              {verb} by {entry.created_by ?? 'an officer'}
            </span>{' '}
            <span className="how">— {entry.reason}</span>
          </div>
        </div>
      ) : null}

      {acting ? (
        <div className="entry-form">
          {acting === 'correct' && canEditValue ? (
            <label>
              As the paper prints it
              <input value={value} onChange={(event) => setValue(event.target.value)} />
            </label>
          ) : null}
          {acting === 'correct' && canEditStatement ? (
            <label>
              In one line
              <input value={statement} onChange={(event) => setStatement(event.target.value)} />
            </label>
          ) : null}
          <label>
            Why
            <input
              value={reason}
              autoFocus
              placeholder={
                acting === 'confirm'
                  ? 'checked against the scan'
                  : acting === 'withdraw'
                    ? 'not what the paper says'
                    : 'the scan reads otherwise'
              }
              onChange={(event) => setReason(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setActing(null)
              }}
            />
          </label>
          <button
            className="btn"
            disabled={!reason.trim() || !changed || act.isPending}
            title={
              !reason.trim()
                ? 'Every correction, confirmation and withdrawal records its reason.'
                : !changed
                  ? 'Nothing has been changed yet. To say it is right, confirm it.'
                  : undefined
            }
            onClick={() => act.mutate()}
          >
            {act.isPending
              ? 'Saving…'
              : acting === 'confirm'
                ? 'Confirm'
                : acting === 'withdraw'
                  ? 'Withdraw'
                  : 'Save the correction'}
          </button>
          <button className="btn" disabled={act.isPending} onClick={() => setActing(null)}>
            Cancel
          </button>
        </div>
      ) : (
        <div className="entry-acts">
          <button className="btn ghost" onClick={() => setActing('confirm')}>
            Confirm
          </button>
          <button
            className="btn ghost"
            disabled={!canEditValue && !canEditStatement}
            title={
              !canEditValue && !canEditStatement
                ? 'The paper’s own words are not retyped here. If they were read wrongly, withdraw the entry.'
                : undefined
            }
            onClick={() => setActing('correct')}
          >
            Correct
          </button>
          <button className="btn ghost bad" onClick={() => setActing('withdraw')}>
            Withdraw
          </button>
        </div>
      )}
    </div>
  )
}

function Discarded({
  items,
  textOrigin,
}: {
  items: DiscardedView[]
  textOrigin: string | null | undefined
}) {
  return (
    <details className="read-sub">
      <summary>Thrown out by the checks · {items.length}</summary>
      <p className="small muted">
        The app proposed these and they failed a check — usually, what it gave is not on the
        page it named. None of them is in the record.
      </p>
      {items.map((item, index) => (
        <div key={index} className="entry">
          <div className="entry-line">
            <span className="deva">{item.text || item.kind}</span>
            <span className="mono faint">{pageWords(item.page, item.page, textOrigin)}</span>
          </div>
          <p className="small muted">{item.reason}</p>
        </div>
      ))}
    </details>
  )
}

function OutOfRecord({
  withdrawn,
  setAside,
  textOrigin,
}: {
  withdrawn: RecordEntryView[]
  setAside: SetAsideView[]
  textOrigin: string | null | undefined
}) {
  return (
    <details className="read-sub">
      <summary>Not in the record · {withdrawn.length + setAside.length}</summary>
      {withdrawn.map((entry) => (
        <div key={entry.id} className="entry">
          <div className="entry-line">
            <span className="deva">{entry.value ?? entry.quote ?? entry.statement}</span>
            <span className="mono faint">
              {pageWords(entry.page_first, entry.page_last, textOrigin)}
            </span>
          </div>
          <p className="small muted">
            Withdrawn by {entry.created_by ?? 'an officer'} — {entry.reason}
          </p>
        </div>
      ))}
      {setAside.map((aside) => (
        <div key={aside.item.id} className="entry">
          <div className="entry-line">
            <span className="deva">
              {aside.item.value ?? aside.item.quote ?? aside.item.statement}
            </span>
            <span className="mono faint">
              {pageWords(aside.item.page_first, aside.item.page_last, textOrigin)}
            </span>
          </div>
          <p className="small muted">{aside.reason}</p>
        </div>
      ))}
    </details>
  )
}
